import { useState } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Stack,
  Divider,
  Zoom,
  FormHelperText,
} from "@mui/material"
import { Mail } from "@mui/icons-material"
import { useUpdateWarmupBulkMutation } from "services/campaign-service"
import toast from "react-hot-toast"

export default function WarmupModal({ open, onClose }) {
  const [email, setEmail] = useState("")
  const [warmupStatus, setWarmupStatus] = useState("enabled")
  const [emailError, setEmailError] = useState("")
  const [updateWarmupBulk, { isLoading }] = useUpdateWarmupBulkMutation();

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  const handleClose = () => {
    setEmail("")
    setWarmupStatus("enabled")
    setEmailError("")
    onClose()
  }

  const handleSubmit = async () => {
    if (!email.trim()) {
      setEmailError("Email is required")
      return
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address")
      return
    }

    try {
      const { message } = await updateWarmupBulk({
        data: {
          email: email.trim().toLowerCase(),
          status: warmupStatus,
        }
      }).unwrap()
      toast.success(message)
      handleClose()
    }
    catch (error) {
      setEmailError(error?.data?.message || "An error occurred while saving the settings")
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
        },
      }}
    >
      <>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              pb: 1,
              background: "linear-gradient(90deg, rgba(25, 118, 210, 0.08) 0%, rgba(66, 165, 245, 0.08) 100%)",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Mail sx={{ color: "primary.main" }} />
              <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                Email Warmup Settings
              </Typography>
            </Stack>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
            Please enter the user’s email address to apply bulk warmup settings to all associated accounts.
          </Typography>

          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              id="email"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError("")
              }}
              error={!!emailError}
              helperText={emailError}
              required
              InputProps={{
                sx: { borderRadius: 2 },
              }}
              disabled={isLoading}
            />

            <FormControl fullWidth disabled={isLoading}>
              <InputLabel id="warmup-status-label">Warmup Status</InputLabel>
              <Select
                labelId="warmup-status-label"
                id="warmup-status"
                value={warmupStatus}
                label="Warmup Status"
                onChange={(e) => setWarmupStatus(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </Select>
              <FormHelperText>
                {warmupStatus === "enabled"
                  ? "Your emails will be gradually warmed up to improve deliverability"
                  : "Email warmup is currently disabled"}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
              boxShadow: "0 4px 14px 0 rgba(25, 118, 210, 0.39)",
              background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
              },
            }}
          >
            {isLoading ? "Saving..." : "Save Settings"}
          </Button>
        </DialogActions>
      </>
    </Dialog>
  )
}